// LanguageSwitcher — toggles /zh <-> /en while PRESERVING the current path + query
// (e.g. /zh/services?ref=x -> /en/services?ref=x), and persists the choice in a
// `lang` cookie so the BFF's "/" default-redirect can honor it on the next visit.
//
// Implementation notes:
//   * Only the leading locale segment is swapped; the rest of pathname + the full
//     search string are carried over verbatim.
//   * The cookie is set on click (the actual language change is driven by the URL
//     via App.tsx's :lang param + i18n.changeLanguage).
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "../i18n";

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

/** Persist the chosen locale for one year so the BFF "/" redirect can honor it. */
function setLangCookie(locale: AppLocale): void {
  if (typeof document === "undefined") return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `lang=${locale}; path=/; max-age=${oneYear}; SameSite=Lax`;
}

export interface LanguageSwitcherProps {
  className?: string;
  /** Optional click handler (e.g. to close a mobile menu after switching). */
  onSwitch?: () => void;
}

export default function LanguageSwitcher({ className, onSwitch }: LanguageSwitcherProps) {
  const { lang } = useParams();
  const location = useLocation();
  const { t } = useTranslation("common");

  const current: AppLocale = isLocale(lang) ? lang : DEFAULT_LOCALE;
  const other: AppLocale = current === "zh" ? "en" : "zh";

  // Swap only the first path segment (the locale prefix); keep path + query.
  const swappedPath = location.pathname.replace(/^\/(zh|en)(?=\/|$)/, `/${other}`);
  const to = `${swappedPath}${location.search}`;

  // Two-segment pill: the active locale is highlighted (a plain, non-link span),
  // the inactive one is the switch <Link> that carries over the path + cookie.
  const labels: Record<AppLocale, string> = { zh: "中", en: "EN" };

  const segmentBase =
    "inline-flex h-7 min-w-[2rem] items-center justify-center rounded-full px-2.5 text-[var(--text-sm)] font-medium leading-none transition-colors";

  return (
    <div
      className={`inline-flex items-center gap-0.5 rounded-full border border-[var(--brand-tint)] bg-white/70 p-0.5${
        className ? ` ${className}` : ""
      }`}
      role="group"
      aria-label={t("lang.label")}
    >
      <span
        className={`${segmentBase} bg-[var(--brand-primary)] text-white`}
        aria-current="true"
        aria-label={t("lang.current")}
      >
        {labels[current]}
      </span>
      <Link
        to={to}
        className={`${segmentBase} text-[var(--brand-slate-text)] hover:bg-[var(--brand-tint-3)] hover:text-[var(--brand-azure-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]`}
        lang={other}
        hrefLang={other === "zh" ? "zh-Hans" : "en"}
        aria-label={t("lang.switchToFull")}
        title={t("lang.switchToFull")}
        onClick={() => {
          setLangCookie(other);
          onSwitch?.();
        }}
      >
        {labels[other]}
      </Link>
    </div>
  );
}
